import { useIntl } from 'react-intl';
import { isFleetLaneCommandVerb, type FleetCommandView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { CommandOutcomeBadge } from './command-outcome-badge';
import { commandStep } from './command-steps';
import { formatCommandLine } from './command-line';
import { useVerbLabel } from './command-verbs';

type Props = {
  readonly commands: ReadonlyArray<FleetCommandView>;
};

/**
 * The machine's `Befehlsverlauf` — a feed, not a control set, so it survives
 * a revoke and may sit below the profile cards (BL-112). One row per issued
 * command, newest first (the list's own order — never re-sorted).
 */
export function CommandActivity({ commands }: Props) {
  const t = useTranslations('pages.admin.fleet.activity');

  if (commands.length === 0) return null;

  return (
    <section>
      <h3 className="mb-2 text-base-sm font-medium text-text-primary">{t('title')}</h3>
      <ul className="flex flex-col gap-2">
        {commands.map((command) => (
          <CommandActivityRow key={command.id} command={command} />
        ))}
      </ul>
    </section>
  );
}

function CommandActivityRow({ command }: { readonly command: FleetCommandView }) {
  const t = useTranslations('pages.admin.fleet.activity');
  const intl = useIntl();
  const verbLabel = useVerbLabel(command.verb);
  const settled = command.outcome !== 'accepted';

  // Seconds, not minutes: the steps (OPS-57) land a second or two apart, and
  // a row reading `10:42 → 10:42 → 10:42` says nothing about where it waited.
  const formatDateTime = (iso: string) => intl.formatDate(iso, { dateStyle: 'short', timeStyle: 'medium' });
  const formatTime = (iso: string) => intl.formatTime(iso, { timeStyle: 'medium' });
  const steps = [
    { key: 'sent', at: command.sentAt },
    { key: 'received', at: command.receivedAt },
    { key: 'running', at: command.startedAt },
    { key: 'settled', at: settled ? command.settledAt : null },
  ] as const;

  const dirtyPaths =
    command.outcome === 'dirty' && command.detail !== null
      ? command.detail
          .split(', ')
          .map((path) => path.trim())
          .filter((path) => path.length > 0)
      : null;

  return (
    <li className="rounded-md border border-border bg-surface p-3">
      {/* Below `sm` the outcome badge gets its own row: it and the verb both
          carry their full text, and side by side one of them has to truncate. */}
      <div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
        <span className="min-w-0 truncate font-mono text-base-sm text-text-primary">
          {/* A host verb names no lane — `sync-branch · engineer` would
              attribute a machine-wide act to whichever card was open. */}
          {command.profile === null
            ? command.action == null
              ? verbLabel
              : `${verbLabel} · ${command.action}`
            : `${verbLabel} · ${command.profile}`}
        </span>
        <span className="hidden flex-1 sm:block" />
        <span className="flex">
          <CommandOutcomeBadge outcome={command.outcome} step={commandStep(command)} />
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-tertiary">
        {command.requestedByName !== null && (
          <span className="min-w-0 truncate">
            {/* A machine-issued command (IMM-939) reads as coming FROM a host,
                not from a person — the row is the only witness to who issued a
                privileged act, and a bare name would read as an operator's. */}
            {t(command.requestedByKind === 'machine' ? 'requested-by-machine' : 'requested-by')}{' '}
            {command.requestedByName}
          </span>
        )}
        {/* One stamp per step the command has reached — issue, sent, received,
            running, settled — so a slow row shows which hop it sat on. */}
        <span className="flex flex-wrap items-center gap-x-1.5" data-testid="command-steps">
          <span className="whitespace-nowrap">{formatDateTime(command.requestedAt)}</span>
          {steps.map(({ key, at }) =>
            at == null ? null : (
              <span key={key} className="whitespace-nowrap">
                → {t(`steps.${key}`)} {formatTime(at)}
              </span>
            ),
          )}
        </span>
      </div>

      {command.verb === 'e2e-unlock' && (
        <div className="mt-1 min-w-0 break-words font-mono text-sm text-text-secondary">
          {command.worker ?? t('e2e-unlock-manual')}
          {command.project != null && ` · ${command.project}`}
        </div>
      )}

      {command.verb === 'run-command' && command.argv != null && (
        <div className="mt-1 min-w-0 break-words font-mono text-sm text-text-secondary">
          {formatCommandLine(command.argv)}
          {command.project != null && ` · ${command.project}`}
        </div>
      )}

      {command.verb === 'engine' && <EngineSummary command={command} />}

      {isFleetLaneCommandVerb(command.verb) || command.verb === 'stop-all' ? <LaneSummary command={command} /> : null}

      {command.verb === 'steer' && command.message != null && (
        <div className="mt-1 min-w-0 whitespace-pre-wrap break-words text-sm text-text-secondary">
          {command.message}
        </div>
      )}

      {dirtyPaths !== null && dirtyPaths.length > 0 && (
        <div className="mt-2 rounded-sm border border-warning-200 bg-warning-50 p-2">
          <div className="text-sm font-medium text-warning-700">{t('dirty-paths', { count: dirtyPaths.length })}</div>
          {/* A `dirty` outcome IS its path list; half a path says nothing, so
              these wrap at every width rather than truncating. */}
          <ul className="mt-1 flex flex-col gap-0.5">
            {dirtyPaths.map((path) => (
              <li key={path} className="min-w-0 break-words font-mono text-sm text-warning-700">
                {path}
              </li>
            ))}
          </ul>
        </div>
      )}

      {dirtyPaths === null && command.detail !== null && (
        <div className="mt-1 min-w-0 break-words text-sm text-text-secondary sm:truncate">{command.detail}</div>
      )}
    </li>
  );
}

/**
 * What an `engine` command set: the engine (or the unset), on which scope, with the models it named. A refusal's
 * reason — lane not declared, CLI not installed — is the row's `detail`, rendered below like any other verb's.
 */
function EngineSummary({ command }: { readonly command: FleetCommandView }) {
  const t = useTranslations('pages.admin.fleet.activity');
  const scope =
    command.lane == null
      ? t('engine-default')
      : command.project != null
        ? t('engine-lane-project', { lane: command.lane, project: command.project })
        : t('engine-lane', { lane: command.lane });
  const models = [
    ...(command.planModel != null ? [t('engine-plan', { model: command.planModel })] : []),
    ...(command.implementModel != null ? [t('engine-implement', { model: command.implementModel })] : []),
  ];

  return (
    <div className="mt-1 min-w-0 break-words font-mono text-sm text-text-secondary">
      {command.engine ?? t('engine-unset')} · {scope}
      {models.length > 0 && ` · ${models.join(' / ')}`}
    </div>
  );
}

/**
 * What narrowed a `start` / `stop` / `restart` / `stop-all` (fleet-agent ADR 0013): the one project a lane verb was
 * narrowed to, and a stop that let the round in hand finish. Nothing renders when neither was said.
 */
function LaneSummary({ command }: { readonly command: FleetCommandView }) {
  const t = useTranslations('pages.admin.fleet.activity');
  const parts = [
    ...(command.project != null ? [t('lane-project', { project: command.project })] : []),
    ...(command.finish === true ? [t('finish')] : []),
  ];

  if (parts.length === 0) return null;

  return <div className="mt-1 min-w-0 break-words text-sm text-text-secondary">{parts.join(' · ')}</div>;
}
